import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Intent, Button, Position } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { DpmsTypes, DpmsTypes_Descriptions } from 'csd.phoenix.models/DpmsTypes';
import { IEsvcsEnrollment } from "csd.phoenix.models/EsvcsEnrollment";
import { EsvcsEnrollmentStatuses, EsvcsEnrollmentStatuses_Descriptions } from 'csd.phoenix.models/EsvcsEnrollmentStatuses';
import { EsvcsProductOfferings_Descriptions } from 'csd.phoenix.models/EsvcsProductOfferings';
import { showToast, dismissToast } from "../../../services/ToasterService";
import { AppModes, useAppMode } from '../../../services/appModeService';
import useAuthService from '../../../services/authService';
import { EnrollmentUpdated, IBulkEnrollmentMessageBody } from '../../../services/hubs/EnrollmentHub';
import { getInstances, getPartners, isPartnerProductOffering } from "../../../stores/enrollmentsStore";
import DataTableContainer from "../../shared/DataTable/DataTableContainer";
import { ContentAlignment, IColumn, IColumnFilters, IColumnSort, SortDirection } from "../../shared/DataTable/models";
import InlineTextInput from "../../shared/InlineTextInput";
import { getDpmsList, getEnrollmentStatusList, getProductList } from "../../shared/ListData";
import MultiSelection from "../../shared/MultiSelection";
import Page from "../../shared/Page";
import SectionLite from "../../shared/Page/SectionLite";
import Selection from "../../shared/Selection";
import { IFieldOption } from "../../shared/form/models";
import StatusUtils from "./StatusUtils";



const getColumns = (isPartnerMode: boolean, isDefaultAccessMode: boolean, onRefresh: () => void, getPartnerName: (partnerId: number | undefined) => string) => {

  const employeeOnlyColumns: IColumn<IEsvcsEnrollment>[] = [
    {
      header: "Partner",
      name: "PartnerId",
      accessor: (data) => (data.PartnerId) ? getPartnerName(data.PartnerId) : data.PartnerId
    },
    {
      header: "Submitted By",
      name: "SubmittedBy",
      accessor: (data) => `${data.SubmittedBy}`
    }
  ];


  const actionColumn: IColumn<IEsvcsEnrollment>[] = [
    {
      header: "",
      name: "RecordId",
      disableSort: true,
      renderer: (id: string, enrollment: IEsvcsEnrollment, _: IColumn<IEsvcsEnrollment, any>) =>
        <div style={{ textAlign: "center" }}>
          <StatusUtils enrollment={enrollment} onRefresh={onRefresh}></StatusUtils>
        </div>
    }
  ];


  const columns: IColumn<IEsvcsEnrollment>[] = [
    {
      header: "Created On",
      name: "RecordCreated",
      renderer: (id: string, enrollment: IEsvcsEnrollment, _: IColumn<IEsvcsEnrollment, any>) =>
        <div title={`Id: ${enrollment.RecordId}`}>
          {enrollment.RecordCreated?.toLocaleString()}
        </div>
    },
    {
      header: "Last Updated On",
      name: "RecordLastUpdated"
    },
    {
      header: "Status",
      name: "EnrollmentStatus",
      accessor: (data) => `${EsvcsEnrollmentStatuses_Descriptions[data.EnrollmentStatus]}`
    },
    {
      header: "SAP - Customer",
      name: "CustomerId",
      accessor: (data) => `${data.CustomerId} - ${data.CustomerName}`,
    },
    {
      header: "DPMS",
      name: "DpmsVersion",
      accessor: (data) => `${DpmsTypes_Descriptions[data.DpmsType]} ${data.DpmsType === DpmsTypes.CMP ? "" : data.DpmsVersion}`
    },
    {
      header: "Hosted?",
      name: "IsHosted",
      styles: { alignment: ContentAlignment.Center },
      accessor: (data) => data.IsHosted
    },
    {
      header: "Time Zone",
      name: "TimeZone"
    },
    {
      header: "Service",
      name: "ProductOffering",
      accessor: (data) => EsvcsProductOfferings_Descriptions[data.ProductOffering]
    },
    ...(isPartnerMode ? [] : employeeOnlyColumns)
    ,
    ...(isDefaultAccessMode ? [] : actionColumn),
  ];


  return columns;
}

//const defaultStatuses = new Set<EsvcsEnrollmentStatuses>([10, 0]);
//const defaultStatuses = new Set<EsvcsEnrollmentStatuses>([EsvcsEnrollmentStatuses.Completed, EsvcsEnrollmentStatuses.New]);
const defaultStatuses = new Set<EsvcsEnrollmentStatuses>([]);

const defaultSort: IColumnSort<IEsvcsEnrollment> = {
  fieldName: "RecordLastUpdated",
  sortDirection: SortDirection.Desc
}

interface IProps {
  pageNum: number;
  pageSize: number;
  customerId?: string;
}

const Comp: React.FC<IProps> = ({ pageNum, pageSize = 15, customerId }) => {
  const [sapNameSelection, setSAPNameSelection] = useState<string>("");
  const [submittedBySelection, setSubmittedBySelection] = useState<string>("");
  const [productSelection, setProductSelection] = useState<string>("");
  const [partnerSelection, setPartnerSelection] = useState<string>("");
  const [statuses, setStatuses] = useState(defaultStatuses);
  const currentFilters = useMemo<IColumnFilters<IEsvcsEnrollment>>(() =>
    ({ EnrollmentStatus: statuses }), [statuses]);
  const { appMode } = useAppMode();
  const isPartnerMode = appMode === AppModes.Partner;
  const [partnerList, setPartnerList] = useState<IFieldOption[]>([]);
  const [dmpsSelections, setDMSPSelections] = useState<string[]>([]);
  const [enrollmentStatusList] = useState<IFieldOption[]>(getEnrollmentStatusList());
  const productList = useMemo(() => {
    const list = getProductList(isPartnerMode, false);
    return [{ value: undefined as any, text: "(Select a product)" }, ...list];
  }, [isPartnerMode]);
  const [dpmsList] = useState<IFieldOption[]>(getDpmsList());
  const [isPartnerSelection, setIsPartnerSelection] = useState<boolean>(false);
  const [selectedProductItemValue, setSelectedProductItemValue] = useState<string | undefined>("");
  const {isInDefaultAccessGroup} = useAuthService();
  const [refreshKey, setRefreshKey] = useState(0);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const refreshAndHideToast = useCallback(() => {
    handleRefresh();
    dismissToast("enrollment-update");
  }, [handleRefresh]);

  const getPartnerName = useCallback((partnerId: number | undefined): string => {
    return partnerList.find(p => p.value === partnerId)?.text ?? '';
  }, [partnerList]);

  const columns = useMemo(() => getColumns(isPartnerMode, isInDefaultAccessGroup(), handleRefresh, getPartnerName),
    [isInDefaultAccessGroup, isPartnerMode, handleRefresh, getPartnerName]);

  const dataLoader = useCallback((pageNum: number, pageSize: number, sorting?: IColumnSort<IEsvcsEnrollment>, filters?: IColumnFilters<IEsvcsEnrollment>) =>
    getInstances([...(filters!.EnrollmentStatus as Set<EsvcsEnrollmentStatuses>).values()], pageNum, pageSize, sorting, customerId, sapNameSelection,
      submittedBySelection, productSelection, partnerSelection, dmpsSelections)
  , [customerId, dmpsSelections, partnerSelection, sapNameSelection, productSelection, submittedBySelection]);

  useEffect(() => {
    async function loadPartnerList() {
      try {
        const loaded = await getPartners();
        if (!loaded.length) {
          showToast("Failed to load partner list!", Intent.DANGER);
        }
        const options = loaded.map((p) => ({ value: p.Id, text: p.Name }));
        options.unshift({ value: undefined as any,  text: "(Select a partner)" });
        setPartnerList(options);
      } catch (e) {
        showToast("Failed to load partner list!", Intent.DANGER);
        setPartnerList([]);
      }
    }

    loadPartnerList();
  }, []);

  useEffect(() => {
    const handler = (payload: IBulkEnrollmentMessageBody) => {
      const customerIds = [...new Set(payload.EnrollmentIds
        .map(id => payload.EnrollmentCustomerMap?.[id])
        .filter((c): c is string => !!c))];
      const maxVisible = 5;
      const visibleIds = customerIds.slice(0, maxVisible);
      const overflow = customerIds.length - maxVisible;
      const toastMessage = customerIds.length > 0
        ? <div>Enrollment(s) updated for customer(s): <ul>{visibleIds.map(c => <li key={c}>{c}</li>)}{overflow > 0 && <li key="__overflow">…and {overflow} more</li>}</ul>Click <Button style={{padding: 0, minHeight: 0, verticalAlign: 'baseline'}} minimal={true} onClick={refreshAndHideToast}>here</Button> to refresh.</div>
        : <div>{payload.EnrollmentIds.length} enrollment(s) updated. Click <Button style={{padding: 0, minHeight: 0, verticalAlign: 'baseline'}} minimal={true} onClick={refreshAndHideToast}>here</Button> to refresh.</div>;
      showToast(
        toastMessage,
        Intent.PRIMARY,
        15000,
        "enrollment-update",
        Position.BOTTOM
      );
    };

    EnrollmentUpdated.addListener(handler);

    return () => {
      EnrollmentUpdated.removeListener(handler);
    };
  }, [refreshAndHideToast]);

  const onSAPNameSelectionChange = useCallback((value: string) => setSAPNameSelection(value), []);

  const onSubmittedBySelectionChange = useCallback((value: string) => setSubmittedBySelection(value), []);

  const onProductSelectionChange = useCallback((value: string) => {
    setProductSelection(value);

    // If this is not a partner program selection then clear out the filter for the partner.
    const productType = Number(value);
    const partnerProgramWasSelected = isPartnerProductOffering(productType);

    !partnerProgramWasSelected && setPartnerSelection("");
    setIsPartnerSelection(partnerProgramWasSelected);
    setSelectedProductItemValue(partnerProgramWasSelected ? partnerSelection : undefined);

  }, [partnerSelection]);

  const onPartnerSelectionChange = useCallback((value: string) => {
    setPartnerSelection(value);
    setSelectedProductItemValue(value);

  }, []);

  const onStatusSelectionChange = useCallback((values: string[]) => setStatuses(new Set<EsvcsEnrollmentStatuses>(values.map(Number))), []);

  const onDPMSsSelectionChange = useCallback((values: string[]) => setDMSPSelections(values), []);


  return (

    <Page header="Enrollment Status">

      <SectionLite
        title="Filter Selections"
        icon={IconNames.FILTER_LIST}
        description=""
        action={<Button icon={IconNames.REFRESH} text="Refresh" onClick={handleRefresh} intent={Intent.PRIMARY} />}
      >
        <div className="columns">
          {/* Enrollment Status */}
          <div className="column">
            <MultiSelection
              items={enrollmentStatusList}
              onChange={onStatusSelectionChange}
              defaultText="(Select statuses)"
            />
          </div>
          {/* DPMS */}
          <div className="column">
            <MultiSelection
              items={dpmsList}
              onChange={onDPMSsSelectionChange}
              defaultText="(Select DPMSs)"
            />
          </div>
        </div>

        <div className="columns">
          {/* SAP ID */}
          <div className="column">
            <InlineTextInput
              value={sapNameSelection}
              fill={true}
              placeholder="Search SAP or Customer Name by keyword"
              allowEdit={true}
              disabled={false}
              onChanged={onSAPNameSelectionChange}
              maxLength={50}
            />
          </div>
          {/* Products */}
          {!isPartnerMode &&
          <div className="column">
            <Selection
              items={productList}
              onChange={onProductSelectionChange}
              //defaultText="(Select a product)"
            />
          </div>}
          {/* Partner */}
          {!isPartnerMode &&
            <div className="column">
            <Selection
              items={partnerList || []}
              onChange={onPartnerSelectionChange}
              //defaultText="(Select a partner)"
              isDisabled={!isPartnerSelection}
              selectedItemValue={selectedProductItemValue!}
              key={productSelection}
              />
            </div>}
          {/* Spacer columns for partner mode */}
          {isPartnerMode &&  <div className="column"></div>}
          {isPartnerMode &&  <div className="column"></div>}
          {isPartnerMode &&  <div className="column"></div>}
          {/* Submitted By */}
          {!isPartnerMode &&
            <div className="column">
              <InlineTextInput
                value={submittedBySelection}
                fill={true}
                placeholder="Search submitted by personnel"
                allowEdit={true}
                disabled={false}
                onChanged={onSubmittedBySelectionChange}
                maxLength={50}
              />
            </div>}

        </div>

      </SectionLite>

      <DataTableContainer
        refreshToken={refreshKey}
        pageSize={pageSize}
        columns={columns}
        dataLoader={dataLoader}
        getRowKey="RecordId"
        filters={currentFilters}
        defaultSort={defaultSort}
        containerStyle={{marginTop: 40}}
      />

    </Page>

  );
}

export default Comp;


